import { inject, Injectable } from '@angular/core';
import { Config } from '@luggagehero/shared/environment';
import { Booking, BookingCategory, IUser } from '@luggagehero/shared/interfaces';
import {
  AppEvent,
  AppEventAction,
  AppPlatform,
  SERVER_TRACKED_ACTIONS,
  SharedAppEventService,
} from '@luggagehero/shared/services/app-events';
import { SharedStorageCriteriaService } from '@luggagehero/shared/services/storage-criteria';
import { SharedWindowService } from '@luggagehero/shared/services/window';
import { cloneDeep } from '@luggagehero/shared/util';
import {
  Angulartics2,
  Angulartics2Facebook,
  Angulartics2GoogleTagManager,
  Angulartics2Intercom,
  PageTrack,
} from 'angulartics2';

const BOOKING_CATEGORIES: readonly BookingCategory[] = ['walk_in', 'same_day', 'advance', 'guest'];

export interface IAnalyticsProperties {
  category?: string;
  label?: string;
  value?: number;
  currency?: string;
  orderId?: string;
  event?: string;
  booking?: Booking;
  user?: IUser;
  storageLocationId?: string;
  customProperties?: unknown;
}

export interface IAnalytics {
  track(action: string, properties: IAnalyticsProperties): void;
}

@Injectable({
  providedIn: 'root',
})
export class SharedAnalyticsService implements IAnalytics {
  private angulartics = inject(Angulartics2);
  private googleTagManager = inject(Angulartics2GoogleTagManager);
  private facebook = inject(Angulartics2Facebook);
  private intercom = inject(Angulartics2Intercom);
  private windowService = inject(SharedWindowService);
  private shopCriteriaService = inject(SharedStorageCriteriaService);
  protected appEventService = inject(SharedAppEventService);

  constructor() {
    this.devMode = Config.isDevelopment;
    this.devMode = false;

    // angulartics2 emits Partial<PageTrack> (path is optional), so guard it.
    this.angulartics.pageTrack.subscribe((e: Partial<PageTrack>) => this.pageTrack(e.path ?? '', undefined));
  }

  public isNative(): boolean {
    return this.windowService.isNative;
  }

  /**
   * Control whether analytics are tracked
   * true: dev mode on, therefore do not track anything
   * false: dev mode off, track everything
   **/
  public get devMode(): boolean {
    return this.angulartics.settings.developerMode;
  }
  public set devMode(value: boolean) {
    this.angulartics.settings.developerMode = value;
  }

  /**
   * Track actions, events, etc.
   **/
  public track(action: string, properties: IAnalyticsProperties): void {
    if (this.devMode) {
      return;
    }

    try {
      this.intercom.eventTrack(action, this.intercomProperties(properties));
    } catch {
      // Catch any unhandled errors as we never want analytics to be able to cause issues
    }

    try {
      this.googleTagManager.eventTrack(action, this.gtmProperties(action, properties));
    } catch {
      // Catch any unhandled errors as we never want analytics to be able to cause issues
    }

    try {
      this.fbEventTrack(action, properties);
    } catch {
      // Catch any unhandled errors as we never want analytics to be able to cause issues
    }

    if (!SERVER_TRACKED_ACTIONS.includes(action)) {
      try {
        void this.appEventService.add(this.appEventProperties(action, properties));
      } catch {
        // Catch any unhandled errors as we never want analytics to be able to cause issues
      }
    }
  }

  /**
   * Called automatically by default with Angular 2 Routing
   * However, that can be turned off and this could be used manually
   **/

  public pageTrack(path: string, _location: unknown): void {
    if (this.devMode) {
      return;
    }

    try {
      this.intercom.pageTrack(path);
      this.googleTagManager.pageTrack(path);
    } catch {
      // Catch any unhanled errors as we never want analytics to be able to cause issues
    }

    try {
      const storageLocationId = this.parseStorageLocationId(path);
      void this.appEventService.add(
        this.appEventProperties(AppEventAction.PageView, { label: path, storageLocationId }),
      );
    } catch {
      // Catch any unhandled errors as we never want analytics to be able to cause issues
    }
  }

  protected parseStorageLocationId(path: string): string | undefined {
    const STORAGE_LOCATION_VIEWS = ['shop-details', 'storage-locations', 'book-shop', 'dropoff', 'places'];
    const OBJECT_ID_REGEX = /^[0-9a-fA-F]{24}$/;

    const segments = path
      .split('?')[0]
      .split(';')[0]
      .split('/')
      .filter((s) => s);

    if (segments.length >= 2 && STORAGE_LOCATION_VIEWS.includes(segments[0]) && OBJECT_ID_REGEX.test(segments[1])) {
      return segments[1];
    }

    return undefined;
  }

  /**
   * Identify authenticated users
   **/
  public identify(properties: { userId: string }): void {
    if (this.devMode) {
      return;
    }

    try {
      if (properties && properties.userId) {
        this.intercom.setUserProperties({ user_id: properties.userId });
        this.googleTagManager.setUsername(properties.userId);
      }
    } catch {
      // Catch any unhandled errors as we never want analytics to be able to cause issues
    }
  }

  private gtmProperties(action: string, properties: IAnalyticsProperties): unknown {
    if (!properties.orderId && !properties.user) {
      return properties;
    }

    const gtmCustom: Record<string, unknown> = {
      orderId: properties.orderId || undefined,
      currencyCode: properties.currency || undefined,
      userEmail: properties.user?.email || undefined,
      userPhone: properties.user?.phone || undefined,
      //
      // The GTM provider only forwards a fixed set of top-level fields plus whatever is in `gtmCustom`, so the booking
      // category has to ride here to reach the dataLayer; it lets GTM split walk-in conversions from pre-bookings.
      // It is read from the booking (deleted below) rather than added as a top-level property, so Intercom and
      // app-events payloads stay unchanged. Like the fields above it is pushed on every event that reaches this point
      // (every booking event, since onBookingEvent always sets orderId), as `undefined` when it does not apply: GTM keeps
      // a key's last value across pushes, and a stale `walk_in` must not carry into a later booking event. Events that
      // take the early return above carry no booking and push no gtmCustom at all, so they leave the key untouched.
      //
      bookingCategory:
        action === (AppEventAction.BookingCreated as string) ? this.bookingCategory(properties.booking) : undefined,
    };

    const gtmProperties = Object.assign({}, properties, { gtmCustom });
    delete gtmProperties.orderId;
    delete gtmProperties.currency;
    delete gtmProperties.booking;
    delete gtmProperties.user;

    return gtmProperties;
  }

  private bookingCategory(booking: Booking | undefined): BookingCategory | undefined {
    const category = booking?.metadata?.['lh_booking_category'];
    return typeof category === 'string' && BOOKING_CATEGORIES.includes(category as BookingCategory)
      ? (category as BookingCategory)
      : undefined;
  }

  private intercomProperties(properties: IAnalyticsProperties): unknown {
    if (!properties.orderId && !properties.booking) {
      return properties;
    }
    const appDomain = Config.isProduction ? 'app.luggagehero.com' : 'app.devheroo.com';
    const bookingId = properties.orderId || properties.booking?._id;

    //
    // Add booking ID with booking URL and clone the provided properties so we're sure we don't affect original objects
    // when we parse and serialize the metadata for Intercom
    //
    const metadata: {
      bookingId: { value: string | undefined; url: string | undefined };
      bookingUrl: string | undefined;
      booking?: Booking;
      price?: { amount: number; currency: string };
    } = {
      ...cloneDeep(properties),
      bookingId: {
        value: bookingId,
        url: bookingId && `https://${appDomain}/bookings/${bookingId}`,
      },
      // TODO: Here for legacy reasons; remove if the above works
      bookingUrl: bookingId && `https://${appDomain}/bookings/${bookingId}`,
    };

    if (metadata.booking?.price.final) {
      //
      // Add a price property according to the specification by Intercom
      // https://developers.intercom.com/intercom-api-reference/v1.4/reference/event-metadata-types
      //
      metadata.price = {
        amount: metadata.booking.price.final.total,
        currency: metadata.booking.price.final.currency,
      };
    }

    // Serialize the metadata (recursively converts properties into types that Intercom supports)
    return this.serializeIntercomMetadata(metadata);
  }

  private serializeIntercomMetadata(metadata: unknown): unknown {
    if (typeof metadata !== 'object') {
      return metadata;
    }
    const rec = metadata as Record<string, unknown>;
    for (const propertyName in rec) {
      const propertyType = typeof rec[propertyName];

      switch (propertyType) {
        case 'string':
          // Keep strings as-is
          break;

        case 'number':
          // Keep numbers as-is
          break;

        case 'boolean':
          // Parse booleans to strings as Intercom doesn't support booleans
          rec[propertyName] = String(rec[propertyName] as boolean);
          break;

        case 'object':
          // Parse the sub object recursively
          rec[propertyName] = this.serializeIntercomMetadata(rec[propertyName]);
          break;

        default:
          // Delete other property types
          delete rec[propertyName];
          break;
      }
    }

    return metadata;
  }

  protected appEventProperties(action: string, properties: IAnalyticsProperties): AppEvent {
    let geoLocation: { lat: number; lon: number } | undefined = undefined;
    if (this.shopCriteriaService.location && this.shopCriteriaService.location.type === 'user') {
      geoLocation = { lat: this.shopCriteriaService.location.lat, lon: this.shopCriteriaService.location.lon };
    }

    let appPlatform: AppPlatform | undefined = undefined;
    if (this.windowService.platform === 'web') {
      appPlatform = AppPlatform.Web;
    } else if (this.windowService.platform === 'android') {
      appPlatform = AppPlatform.Android;
    } else if (this.windowService.platform === 'ios') {
      appPlatform = AppPlatform.iOS;
    }

    const appVersion = this.windowService.appVersion;
    const appName = this.windowService.appName;

    const appEventProperties = Object.assign({}, properties, {
      appPlatform,
      appVersion,
      appName,
      geoLocation,
      action,
    });
    delete appEventProperties.event;
    delete appEventProperties.booking;
    delete appEventProperties.user;

    return appEventProperties;
  }

  private fbEventTrack(action: string, properties: IAnalyticsProperties): void {
    //
    // Facebook event list:
    //
    //   'ViewContent'
    //   'Search'
    //   'AddToCart'
    //   'AddToWishlist'
    //   'InitiateCheckout'
    //   'AddPaymentInfo'
    //   'Purchase'
    //   'Lead'
    //   'CompleteRegistration'
    //
    switch (action) {
      case 'signUp':
      case 'CompleteRegistration':
        this.facebook.eventTrack('CompleteRegistration', {});
        break;

      case 'bookingCreated':
      case 'MakeBooking':
        this.facebook.eventTrack('AddToCart', { content_name: properties.label });
        break;

      case 'paymentMethodAdded':
        this.facebook.eventTrack('AddPaymentInfo', {});
        break;

      case 'bagsDroppedOff':
        this.facebook.eventTrack('InitiateCheckOut', {});
        break;

      case 'bagsPickedUp':
      case 'CompleteCheckOut':
        this.facebook.eventTrack('Purchase', { currency: properties.currency, value: properties.value });
        break;

      default: {
        const facebookProperties = Object.assign({}, properties);
        delete facebookProperties.booking;
        delete facebookProperties.user;

        this.facebook.eventTrack(action, properties);

        break;
      }
    }
  }
}

/**
 * Base class
 * Standardizes tracking actions and categorization
 */
export class Analytics implements IAnalytics {
  public analytics = inject(SharedAnalyticsService);

  // sub-classes should define their category
  public category!: string;

  /**
   * Track actions, events, etc.
   **/
  track(action: string, properties: IAnalyticsProperties): void {
    this.analytics.track(action, Object.assign(properties, { category: this.category }));
  }
}
